import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";

import {
  LOCALE_STORAGE_KEY,
  resolveDefaultLocale,
} from "./i18n/localeConfig";
import { installDeployReloadGuard } from "./lib/deployReloadGuard";
import { isLandingPath } from "./lib/landing-path";
import "./styles.css";

// M20.1 P0 — self-heal stale lazy chunks after a deploy. Installed as early as
// possible so a preload failure during boot is recovered with a one-time reload.
// Since the bundle split BOTH boot branches below are lazy chunks, so the guard
// now also covers the main-app/landing bootstrap itself.
installDeployReloadGuard();

/*
 * Bundle split (perf surgery): the entry stays tiny (react + react-dom + this
 * file) and picks ONE of two lazy boot branches by pathname:
 *
 * - /lazecast…  → LandingRoot: the isolated landing + PublicAuthProvider only.
 *                 Never pulls the app shell, v1/v2 screens, react-router or the
 *                 app's locale registry — those all live behind the App chunk.
 *                 (The landing lazy-loads its own dicts via lazecast/lib/labels.)
 * - everything else → App: the full SPA, unchanged (it still owns a lazy
 *                 /lazecast/* route for client-side navigation INTO the
 *                 landing, which resolves to the same shared chunk).
 *
 * The branch is decided before either import starts, so the two graphs never
 * mix on cold load. styles.css stays in the entry for BOTH branches — the
 * landing's scoped theme relies on the global body/reset layer exactly as it
 * did when the entry was monolithic (CSS is not part of the JS budget).
 */
const App = lazy(() => import("./App").then((m) => ({ default: m.App })));
const LandingRoot = lazy(() => import("./LandingRoot"));

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found in index.html");
}

const landing = isLandingPath(window.location.pathname);

/*
 * M33 — start the active locale's dict BEFORE React mounts.
 *
 * `I18nProvider` holds the first paint until the dict resolves. Waiting for App
 * to arrive and only then discovering the locale would serialize two round trips;
 * kicking it off here puts the ~18KB locale chunk in flight alongside the ~147KB
 * (gzip) App chunk, so it is ready by the time App renders.
 *
 * Two deliberate details:
 *  - The app branch only. The landing owns a separate, smaller dict path and must
 *    not pull the app's locale registry into its graph.
 *  - A DYNAMIC import, so the statically-bundled `en` fallback dict stays out of
 *    the entry chunk (which both branches share).
 * Detection here must stay in sync with I18nProvider's; both call the same
 * dict-free `resolveDefaultLocale`, so they cannot diverge.
 */
if (!landing) {
  void import("./i18n/locales").then(({ loadLocale }) => {
    let persisted: string | null = null;
    try {
      persisted = localStorage.getItem(LOCALE_STORAGE_KEY);
    } catch {
      /* storage unavailable (private mode) — fall through to navigator. */
    }
    const languages =
      navigator.languages && navigator.languages.length > 0
        ? navigator.languages
        : navigator.language
          ? [navigator.language]
          : undefined;
    // A rejected preload is not an error here: I18nProvider retries and, failing
    // that, degrades to `en`.
    void loadLocale(resolveDefaultLocale(persisted, languages)).catch(
      () => undefined,
    );
  });
}

createRoot(rootElement).render(
  <StrictMode>
    <Suspense fallback={null}>{landing ? <LandingRoot /> : <App />}</Suspense>
  </StrictMode>,
);

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch((err) => {
        console.warn("SW registration failed:", err);
      });
  });
}
